/**
 * Local persistence: streak, stats and per-day results.
 *
 * Everything lives in localStorage and nothing is sent anywhere. Every read
 * and write is wrapped, because localStorage throws in private windows and
 * anywhere the user has blocked site data — the game must still be playable,
 * just forgetful, rather than broken.
 */

import { Grade } from '../game/grade';

const KEY_STATS = 'prism.v1.stats';
const KEY_RESULTS = 'prism.v1.results';
const VERSION = 1;

export interface DayResult {
  index: number;
  grade: Grade;
  distance: number;
  tolerance: number;
  blends: number;
  ms: number;
  hex: string;
  targetHex: string;
  at: number;
}

export interface Stats {
  version: number;
  streak: number;
  maxStreak: number;
  played: number;
  wins: number;
  grades: Record<Grade, number>;
  /** Tightest match ever, in scaled OKLab units. */
  bestDistance: number | null;
  totalDistance: number;
  /** Day index of the most recent result, or null. Drives streak continuity. */
  lastIndex: number | null;
  muted: boolean;
}

function emptyGrades(): Record<Grade, number> {
  return { perfect: 0, excellent: 0, great: 0, close: 0, miss: 0 };
}

export function emptyStats(): Stats {
  return {
    version: VERSION,
    streak: 0,
    maxStreak: 0,
    played: 0,
    wins: 0,
    grades: emptyGrades(),
    bestDistance: null,
    totalDistance: 0,
    lastIndex: null,
    muted: false,
  };
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* full, blocked, or private. Nothing to do but carry on. */
  }
}

export function loadStats(): Stats {
  const raw = read<Stats>(KEY_STATS);
  if (!raw || raw.version !== VERSION) return emptyStats();
  // Merge so a schema addition does not wipe an existing player.
  return { ...emptyStats(), ...raw, grades: { ...emptyGrades(), ...raw.grades } };
}

export function loadResults(): DayResult[] {
  const raw = read<DayResult[]>(KEY_RESULTS);
  return Array.isArray(raw) ? raw.filter(isResult) : [];
}

function isResult(r: unknown): r is DayResult {
  return !!r && typeof r === 'object' && typeof (r as DayResult).index === 'number';
}

export function resultFor(index: number): DayResult | null {
  return loadResults().find((r) => r.index === index) ?? null;
}

export function hasPlayed(index: number): boolean {
  return resultFor(index) !== null;
}

/**
 * Record a finished day and fold it into the streak.
 *
 * Stats are recomputed from the full result log rather than patched in place,
 * because a day can be *replayed*. Patching would leave a grade counted twice,
 * or a distance still in the average after it was beaten, or a streak that
 * survived a miss it should have died from. At a few hundred results this
 * costs nothing and is the only version that is obviously right.
 */
export function recordResult(result: DayResult): Stats {
  const results = loadResults();
  const existing = results.findIndex((r) => r.index === result.index);

  if (existing >= 0) results[existing] = result;
  else results.push(result);

  results.sort((a, b) => a.index - b.index);
  write(KEY_RESULTS, results.slice(-400));

  const stats = rollUp(results.slice(-400), loadStats().muted);
  write(KEY_STATS, stats);
  return stats;
}

/** Derive every stat from the result log. Pure, so it can be re-derived at will. */
function rollUp(results: readonly DayResult[], muted: boolean): Stats {
  const stats = emptyStats();
  stats.muted = muted;
  let lastWinIndex: number | null = null;

  for (const r of results) {
    stats.played++;
    stats.grades[r.grade]++;

    if (r.grade === 'miss') {
      stats.streak = 0;
      lastWinIndex = null;
      continue;
    }

    stats.wins++;
    stats.totalDistance += r.distance;
    if (stats.bestDistance === null || r.distance < stats.bestDistance) {
      stats.bestDistance = r.distance;
    }

    // A streak only survives if the immediately preceding calendar day was
    // also won: an unplayed day breaks it just as surely as a miss.
    stats.streak = lastWinIndex !== null && r.index === lastWinIndex + 1 ? stats.streak + 1 : 1;
    stats.maxStreak = Math.max(stats.maxStreak, stats.streak);
    lastWinIndex = r.index;
  }

  stats.lastIndex = results.length ? results[results.length - 1].index : null;
  return stats;
}

/**
 * Whether the streak is still alive right now.
 *
 * Once a day is missed the stored streak is stale, so the UI needs to know
 * it is already dead rather than showing a number that cannot be defended.
 */
export function streakIsAlive(stats: Stats, todayIndex: number): boolean {
  if (stats.lastIndex === null) return stats.streak > 0;
  return stats.lastIndex === todayIndex || stats.lastIndex === todayIndex - 1;
}

export function recent(days: number, todayIndex: number): Array<DayResult | { index: number }> {
  const results = new Map(loadResults().map((r) => [r.index, r]));
  const out: Array<DayResult | { index: number }> = [];
  for (let i = todayIndex; i > todayIndex - days && i >= 0; i--) {
    out.push(results.get(i) ?? { index: i });
  }
  return out;
}

/** Mean distance across winning days, or null before the first win. */
export function averageDistance(stats: Stats): number | null {
  return stats.wins > 0 ? stats.totalDistance / stats.wins : null;
}

export function clearAll(): void {
  try {
    localStorage.removeItem(KEY_STATS);
    localStorage.removeItem(KEY_RESULTS);
  } catch {
    /* nothing to do */
  }
}

export function setMuted(muted: boolean): void {
  const stats = loadStats();
  stats.muted = muted;
  write(KEY_STATS, stats);
}

export function isMuted(): boolean {
  return loadStats().muted;
}
